import type { TypeId } from "./types.ts";
import { SIDECHAIN_SPECIES, SIDECHAIN_SPECIES_IDS } from "./speciesSidechain.ts";
import { LEVEL_UP } from "./learnsetsLate.ts";

export const SPECIES_IDS = [
  "bogfiend",
  "shibonk",
  "wojakle",
  "gigachomp",
  "laserpup",
  "whalord",
  "apeling",
  "rugfox",
  "moonling",
  "chopperdog",
  "shibonkx",
  "bogbaron",
  "wojaked",
  "laserhound",
  "rugwolf",
  "apemajor",
  "moonlord",
  "chopperalpha",
  "cloutling",
  "cloutking",
  "hopiumite",
  "copiumax",
  "chartgob",
  "candlewyrm",
  "bagsnail",
  "diamondshell",
  "fomoling",
  "fomobeast",
  "gasgob",
  "slippeel",
  "mintmoth",
  "airdropqueen",
  "yieldyeti",
  "yapling",
  "yapstorm",
  "lurkit",
  "threadmaw",
  "ratiofin",
  "ratioking",
  "scrollmite",
  "doomwyrm",
  "finekit",
  "kilnmutt",
  "npcat",
  "lorelord",
  "gatekit",
  "gatewyrm",
  "glazeling",
  "glazewyrm",
  "capling",
  "nocapra",
  "grassnudge",
  "lawnlord",
  "seethtoad",
  "maldmaw",
  "deluling",
  "delulord",
  "vibeling",
  "vibewyrm",
  "draftkit",
  "postwyrm",
  "spoilpup",
  "leakmaw",
  "queueling",
  "linebaron",
  "receiptling",
  "auditlord",
  "clipfinch",
  "cliplord",
  "toquelin",
  "tuquelord",
  "poutkit",
  "gravylord",
  "ghostkit",
  "ghostwyrm",
  "crashpup",
  "crashmaw",
  "sauceling",
  "saucelord",
  "pingling",
  "pinglord",
  "slayling",
  "slaylord",
  "cookling",
  "cookwyrm",
  "midling",
  "midbaron",
  "lockkit",
  "locklord",
  "depkit",
  "depbaron",
  "ehcorn",
  "ehvergreen",
  "snoozlet",
  "napocalypse",
  "coneling",
  "detourant",
  "syrupup",
  "sapreme",
  "bufferret",
  "lagomorph",
  "honklet",
  "honkference",
  "chillip",
  "chillossus",
  "crumbit",
  "breadwinner",
  "puckup",
  "clapback",
  "tabbit",
  "tabalanche",
  // Frontier: five biomes east of Yield Yard (BURN / HODL / RUG).
  "frostbag",
  "glacierbag",
  "pengwallet",
  "coldvault",
  "icepup",
  "diamondpaws",
  "candlecub",
  "wickwolf",
  "shillpion",
  "shillscorp",
  "sprikle",
  "saguarocket",
  "humpfund",
  "emberbit",
  "blazebit",
  "infernomint",
  "gasbull",
  "magmabull",
  "moltwisp",
  "ruglet",
  "rugwraith",
  "mirebot",
  "mireshill",
  "botfly",
  "swarmlord",
  "honeytoad",
  "honeybaron",
  "shardling",
  "shardking",
  "prismpup",
  "prismhound",
  "nodelet",
  "validrake",
  "cachemole",
  "vaultmole",
  "genesiwyrm",
  "ursarug",
  "chatbit",
  "chatlord",
  "glitchpup",
  "glitchhound",
  "fanbit",
  "fanstorm",
  "ledmoth",
  "rackmoth",
  "neuralith",
  "pendbat",
  "stuckwing",
  "shroomint",
  "shroomancer",
  "hashgrub",
  "asicarab",
  "geodeggo",
  "geodrake",
  "shrimpling",
  "krillionaire",
  "sidecrab",
  "crabstack",
  "jellyfud",
  "medusell",
  "pearlclam",
  "lunapup",
  "lunawolf",
  "rocketot",
  "rocketitan",
  "nimbaa",
  "nimbram",
  "craterling",
  "selenarch",
  ...SIDECHAIN_SPECIES_IDS,
] as const;

export type SpeciesId = (typeof SPECIES_IDS)[number];

export interface SpeciesDef {
  id: SpeciesId;
  name: string;
  types: TypeId[];
  hp: number;
  atk: number;
  def: number;
  spd: number;
  /** Near-faint Meme Tin catch chance in percent, before health/quality scaling. */
  catch: number;
  bounty: number;
  moves: [string, string, string, string];
  /**
   * Level-up offers. Early lines list their kit first and one mid-teen id outside it; later offers
   * (learnsetsLate.ts) are merged in at load. A full kit turns an offer into learn/forget/skip, and
   * wild or trainer memelings mint with the offers their level has reached.
   */
  learnset?: { level: number; id: string }[];
  /** Replace only inherited moves; custom slots survive evolution and legacy loading. */
  moveUpgrades?: Record<string, string>;
  blurb: string;
  cry: string;
  evolvesTo?: SpeciesId;
  evolveLevel?: number;
}

export const SPECIES: Record<SpeciesId, SpeciesDef> = {
  bogfiend: {
    id: "bogfiend",
    evolvesTo: "bogbaron",
    evolveLevel: 18,
    name: "Bogfiend",
    types: ["liquid", "cope"],
    hp: 58,
    atk: 52,
    def: 62,
    spd: 40,
    catch: 48,
    bounty: 18,
    moves: ["ribbitRug", "pondPump", "stickyHands", "copeSplash"],
    learnset: [{ level: 1, id: "ribbitRug" }, { level: 1, id: "pondPump" },
      { level: 9, id: "stickyHands" }, { level: 14, id: "copeSplash" },
      { level: 17, id: "marshDue" }],
    blurb: "Pond clerk. Stolen bathmat. Croaks only when the water owes him. It usually does.",
    cry: "rib-RUG",
  },
  shibonk: {
    id: "shibonk",
    evolvesTo: "shibonkx",
    evolveLevel: 16,
    name: "Shibonk",
    types: ["viral", "flex"],
    hp: 44,
    atk: 58,
    def: 40,
    spd: 62,
    catch: 45,
    bounty: 20,
    moves: ["bonk", "suchSpeed", "rareTreat", "moonHowl"],
    learnset: [{ level: 1, id: "bonk" }, { level: 1, id: "suchSpeed" },
      { level: 8, id: "rareTreat" }, { level: 12, id: "moonHowl" },
      { level: 15, id: "crossBaton" }],
    blurb: "Late-shift baton dog. Bonks the joke so it keeps walking home. Clock out later.",
    cry: "BONK",
  },
  wojakle: {
    id: "wojakle",
    evolvesTo: "wojaked",
    evolveLevel: 16,
    name: "Wojakle",
    types: ["cope", "chaos"],
    hp: 54,
    atk: 54,
    def: 46,
    spd: 54,
    catch: 55,
    bounty: 16,
    moves: ["feelsBad", "panicSell", "hopium", "existentialStare"],
    learnset: [{ level: 1, id: "feelsBad" }, { level: 1, id: "panicSell" },
      { level: 8, id: "hopium" }, { level: 12, id: "existentialStare" },
      { level: 15, id: "bottleStorm" }],
    blurb: "Refreshes a chart that is not there. The tuque is load-bearing. The chart is not.",
    cry: "feels...",
  },
  gigachomp: {
    id: "gigachomp",
    name: "Gigachomp",
    types: ["flex"],
    hp: 58,
    atk: 70,
    def: 62,
    spd: 32,
    catch: 34,
    bounty: 28,
    moves: ["jawlineFlex", "absoluteCinema", "silentNod", "grindset"],
    blurb: "Jaw first, sentence never. Speaks entirely in stills. The path clocks out when he nods.",
    cry: "...",
  },
  laserpup: {
    id: "laserpup",
    evolvesTo: "laserhound",
    evolveLevel: 18,
    name: "Laserpup",
    types: ["signal", "viral"],
    hp: 42,
    atk: 60,
    def: 40,
    spd: 68,
    catch: 45,
    bounty: 22,
    moves: ["laserGlare", "diamondBark", "airdropFetch", "trendChase"],
    learnset: [{ level: 1, id: "laserGlare" }, { level: 1, id: "diamondBark" },
      { level: 9, id: "airdropFetch" }, { level: 14, id: "trendChase" },
      { level: 17, id: "prismFang" }],
    blurb: "Diamond-cut pupils. Fetches bags nobody threw. The dock is still empty.",
    cry: "yip-ZAP",
  },
  whalord: {
    id: "whalord",
    name: "Whalord",
    types: ["liquid", "flex"],
    hp: 72,
    atk: 64,
    def: 58,
    spd: 28,
    catch: 22,
    bounty: 40,
    moves: ["marketCapsize", "deepBuy", "breach", "liquidityWave"],
    blurb: "Crowned in pond-gold. Buys the dip until the dip is the weather. The crown invoices the rain.",
    cry: "OOOOM",
  },
  apeling: {
    id: "apeling",
    evolvesTo: "apemajor",
    evolveLevel: 20,
    name: "Apeling",
    types: ["viral", "chaos"],
    hp: 50,
    atk: 62,
    def: 44,
    spd: 46,
    catch: 42,
    bounty: 24,
    moves: ["bananaHands", "floorTogether", "chestBeat", "fomoSwing"],
    blurb: "Banana diplomacy: both palms, one yell, no exit interview. The peel is the treaty. Nobody signed it.",
    cry: "HOO-HOO",
  },
  rugfox: {
    id: "rugfox",
    evolvesTo: "rugwolf",
    evolveLevel: 20,
    name: "Rugfox",
    types: ["chaos", "signal"],
    hp: 40,
    atk: 66,
    def: 36,
    spd: 70,
    catch: 42,
    bounty: 26,
    moves: ["pullTheCord", "fakeChart", "smokeExit", "trustMeBro"],
    blurb: "Smile, plug, gone. The cord was décor until the lights agreed. They agreed.",
    cry: "hee-kss",
  },
  moonling: {
    id: "moonling",
    evolvesTo: "moonlord",
    evolveLevel: 22,
    name: "Moonling",
    types: ["signal", "flex"],
    hp: 46,
    atk: 54,
    def: 42,
    spd: 60,
    catch: 40,
    bounty: 24,
    moves: ["liftoff", "zeroGravity", "craterStomp", "orbitTaunt"],
    blurb: "A crater, a flag, a polite orbit. Ambition, size extra-small. The flag is on time.",
    cry: "woo-OP",
  },
  chopperdog: {
    id: "chopperdog",
    evolvesTo: "chopperalpha",
    evolveLevel: 22,
    name: "Chopperdog",
    types: ["viral", "liquid"],
    hp: 54,
    atk: 56,
    def: 48,
    spd: 52,
    catch: 40,
    bounty: 30,
    moves: ["cashRain", "hoverTax", "rotorSlap", "bailout"],
    blurb: "Rotary taxman. Rains invoices. The air already owes him. Plus hover.",
    cry: "CHOP-chop",
  },
  shibonkx: {
    id: "shibonkx", name: "Shibonkx", types: ["viral", "flex"],
    hp: 66, atk: 83, def: 62, spd: 86, catch: 25, bounty: 38,
    moves: ["crossBaton", "suchSpeed", "rareTreat", "nightSignal"],
    moveUpgrades: { bonk: "crossBaton", moonHowl: "nightSignal" },
    blurb: "Night watch now. Two batons, one joke. Late candles file a complaint.", cry: "BONK-BONK",
  },
  bogbaron: {
    id: "bogbaron", name: "Bogbaron", types: ["liquid", "cope"],
    hp: 84, atk: 74, def: 86, spd: 50, catch: 24, bounty: 40,
    moves: ["ribbitRug", "reedDecree", "stickyHands", "marshDue"],
    moveUpgrades: { pondPump: "reedDecree", copeSplash: "marshDue" },
    blurb: "Promoted himself. The bathmat has fringe and a title. The pond pays rent.", cry: "brr-RUG",
  },
  wojaked: {
    id: "wojaked", name: "Wojaked", types: ["cope", "chaos"],
    hp: 80, atk: 76, def: 68, spd: 72, catch: 28, bounty: 36,
    moves: ["bottleStorm", "lastNerve", "hopium", "existentialStare"],
    moveUpgrades: { feelsBad: "bottleStorm", panicSell: "lastNerve" },
    blurb: "Bottled the rain. Sells it back as courage. The tuque still refreshes. Still here.", cry: "still here",
  },
  laserhound: {
    id: "laserhound", name: "Laserhound", types: ["signal", "viral"],
    hp: 64, atk: 82, def: 63, spd: 88, catch: 22, bounty: 42,
    moves: ["prismFang", "diamondBark", "echoFetch", "trendChase"],
    moveUpgrades: { laserGlare: "prismFang", airdropFetch: "echoFetch" },
    blurb: "The visor got a career. Fetches echoes. The bag never left the dock.", cry: "AR-ZAP",
  },
  rugwolf: {
    id: "rugwolf", name: "Rugwolf", types: ["chaos", "signal"],
    hp: 66, atk: 86, def: 60, spd: 88, catch: 22, bounty: 46,
    moves: ["cordSever", "falseBeacon", "smokeExit", "trustMeBro"],
    moveUpgrades: { pullTheCord: "cordSever", fakeChart: "falseBeacon" },
    blurb: "The rug learned teeth. Three prongs, one bad promise. The outlet kept the tip.", cry: "krr-KSS",
  },
  apemajor: {
    id: "apemajor", name: "Apemajor", types: ["viral", "chaos"],
    hp: 82, atk: 91, def: 73, spd: 57, catch: 22, bounty: 44,
    moves: ["canopyCrash", "floorTogether", "chestBeat", "drumRoll"],
    moveUpgrades: { bananaHands: "canopyCrash", fomoSwing: "drumRoll" },
    blurb: "The floor held. Now he drums policy from a banana rank. Minutes: bruises.", cry: "HOO-ROOM",
  },
  moonlord: {
    id: "moonlord", name: "Moonlord", types: ["signal", "flex"],
    hp: 74, atk: 82, def: 73, spd: 79, catch: 20, bounty: 48,
    moves: ["orbitLance", "zeroGravity", "flagfall", "orbitTaunt"],
    moveUpgrades: { liftoff: "orbitLance", craterStomp: "flagfall" },
    blurb: "Flags its own shadow. The orbit stayed polite. The lance billed the crater.", cry: "woo-ORBIT",
  },
  chopperalpha: {
    id: "chopperalpha", name: "Choppercap", types: ["viral", "liquid"],
    hp: 84, atk: 81, def: 75, spd: 71, catch: 18, bounty: 50,
    moves: ["rainFreight", "hoverTax", "twinRotor", "bailout"],
    moveUpgrades: { cashRain: "rainFreight", rotorSlap: "twinRotor" },
    blurb: "Twin rotors, same invoice. Weather is a line item now. Plus hover.", cry: "CHOP-CHOP",
  },
  cloutling: {
    id: "cloutling", name: "Cloutling", types: ["viral"],
    evolvesTo: "cloutking", evolveLevel: 16,
    hp: 42, atk: 46, def: 36, spd: 62, catch: 60, bounty: 18,
    moves: ["ringFlash", "wingClip", "hypeLoop", "confettiBurst"],
    learnset: [{ level: 1, id: "ringFlash" }, { level: 1, id: "wingClip" },
      { level: 8, id: "hypeLoop" }, { level: 12, id: "confettiBurst" },
      { level: 15, id: "chartFlare" }],
    blurb: "Brings its own ring light to a marsh. The reeds subscribed. The frogs sat closer.", cry: "ping-ping",
  },
  cloutking: {
    id: "cloutking", name: "Cloutking", types: ["viral", "flex"],
    hp: 66, atk: 76, def: 58, spd: 88, catch: 26, bounty: 40,
    moves: ["ringFlash", "wingClip", "hypeLoop", "confettiBurst"],
    blurb: "Encore visible from Stubtown. The frogs renewed. The light did not.", cry: "en-CORE",
  },
  hopiumite: {
    id: "hopiumite", name: "Hopiumite", types: ["cope"],
    evolvesTo: "copiumax", evolveLevel: 18,
    hp: 54, atk: 40, def: 56, spd: 36, catch: 65, bounty: 16,
    moves: ["greenWick", "steadyShell", "candleSip", "copeCrash"],
    learnset: [{ level: 1, id: "greenWick" }, { level: 1, id: "copeCrash" },
      { level: 9, id: "steadyShell" }, { level: 14, id: "candleSip" },
      { level: 17, id: "waxLance" }],
    blurb: "One maybe-flame under a leaf. Calls that a plan. The leaf asked for a second source.", cry: "maybe",
  },
  copiumax: {
    id: "copiumax", name: "Copiumax", types: ["cope", "chaos"],
    hp: 82, atk: 71, def: 85, spd: 42, catch: 24, bounty: 42,
    moves: ["greenWick", "steadyShell", "candleSip", "copeCrash"],
    blurb: "Wax plates, same maybe. The rain clocked out first. The wick stayed late.", cry: "still-lit",
  },
  chartgob: {
    id: "chartgob", name: "Chartgob", types: ["chaos"],
    evolvesTo: "candlewyrm", evolveLevel: 20,
    hp: 44, atk: 59, def: 37, spd: 54, catch: 48, bounty: 22,
    moves: ["wickWhip", "chartFlare", "trendTilt", "candleBreath"],
    blurb: "Ink-black feet. Draws tomorrow in wax. Bills yesterday at closing. The wick keeps the books.", cry: "tick-TICK",
  },
  candlewyrm: {
    id: "candlewyrm", name: "Candlewyrm", types: ["chaos", "flex"],
    hp: 76, atk: 89, def: 65, spd: 73, catch: 20, bounty: 46,
    moves: ["waxLance", "chartFlare", "trendTilt", "canopyFlare"],
    moveUpgrades: { wickWhip: "waxLance", candleBreath: "canopyFlare" },
    blurb: "The wick grew a spine. Each turn leaves a warm rumor. The stone files it.", cry: "fwoom",
  },
  bagsnail: {
    id: "bagsnail", name: "Bagsnail", types: ["liquid"],
    evolvesTo: "diamondshell", evolveLevel: 17,
    hp: 56, atk: 43, def: 61, spd: 22, catch: 60, bounty: 18,
    moves: ["paperCut", "bagBrine", "foldGuard", "diamondPress"],
    learnset: [{ level: 1, id: "paperCut" }, { level: 1, id: "bagBrine" },
      { level: 8, id: "foldGuard" }, { level: 13, id: "diamondPress" },
      { level: 16, id: "deepBuy" }],
    blurb: "Paper-bag house, waterline lease. Packed before the lease asked. The bag filed first.", cry: "crinkle",
  },
  diamondshell: {
    id: "diamondshell", name: "Diamondshell", types: ["liquid", "flex"],
    hp: 88, atk: 72, def: 92, spd: 32, catch: 22, bounty: 44,
    moves: ["paperCut", "bagBrine", "foldGuard", "diamondPress"],
    blurb: "The bag calcified. Holds the bridge like it signed a lease. The caisse still knocks.", cry: "clink-clonk",
  },
  fomoling: {
    id: "fomoling", name: "Fomoling", types: ["viral", "cope"],
    evolvesTo: "fomobeast", evolveLevel: 19,
    hp: 43, atk: 51, def: 33, spd: 67, catch: 58, bounty: 20,
    moves: ["rushPeck", "queueCry", "refresh", "herdRush"],
    learnset: [{ level: 1, id: "rushPeck" }, { level: 1, id: "queueCry" },
      { level: 9, id: "refresh" }, { level: 15, id: "herdRush" },
      { level: 18, id: "stampede" }],
    blurb: "Receipt for a tail. Joins every queue. Asks why from the front.", cry: "wait-ME",
  },
  fomobeast: {
    id: "fomobeast", name: "Fomobeast", types: ["viral", "chaos"],
    hp: 78, atk: 80, def: 64, spd: 86, catch: 24, bounty: 44,
    moves: ["rushPeck", "queueCry", "refresh", "herdRush"],
    blurb: "Arrives first at yesterday's closing bell. Still in the wrong line. The receipt agrees.", cry: "ALL-IN",
  },
  gasgob: {
    id: "gasgob", name: "Gasgob", types: ["chaos"],
    hp: 62, atk: 68, def: 57, spd: 46, catch: 42, bounty: 30,
    moves: ["feeBite", "sootSpit", "meterUp", "ventBeam"],
    blurb: "Chimney hat, itemized cough. Bills the smoke you already paid. Plus tip.", cry: "toll-TOLL",
  },
  slippeel: {
    id: "slippeel", name: "Slippeel", types: ["liquid"],
    hp: 54, atk: 57, def: 43, spd: 81, catch: 42, bounty: 26,
    moves: ["spreadSnap", "slipstream", "thinPool", "brineSiphon"],
    blurb: "Offset fins. Lands one puddle past the quote. Calls the miss a spread. The quote moved.", cry: "sliiip",
  },
  mintmoth: {
    id: "mintmoth", name: "Mintmoth", types: ["viral"],
    evolvesTo: "airdropqueen", evolveLevel: 18,
    hp: 45, atk: 48, def: 40, spd: 59, catch: 58, bounty: 20,
    moves: ["ticketToss", "mintRay", "capeGuard", "claimDrift"],
    learnset: [{ level: 1, id: "ticketToss" }, { level: 1, id: "mintRay" },
      { level: 9, id: "capeGuard" }, { level: 14, id: "claimDrift" },
      { level: 17, id: "airdropFetch" }],
    blurb: "Perforates leaves, calls them limited. Box office: the canopy. No will-call.", cry: "mint-mint",
  },
  airdropqueen: {
    id: "airdropqueen", name: "Airdropqueen", types: ["viral", "flex"],
    hp: 73, atk: 78, def: 68, spd: 83, catch: 24, bounty: 44,
    moves: ["ticketToss", "mintRay", "capeGuard", "claimDrift"],
    blurb: "Parachute cape, tiny grants, empty nests. Fine print lands last. Always.", cry: "for-YOU",
  },
  yieldyeti: {
    id: "yieldyeti", name: "Yieldyeti", types: ["flex", "liquid"],
    hp: 86, atk: 72, def: 74, spd: 27, catch: 26, bounty: 42,
    moves: ["snowStake", "thawDrip", "warmReserve", "driftWall"],
    blurb: "Stacks snow in neat piles. The thaw is the dividend. The tuque does the books.", cry: "snow-slow",
  },
  yapling: {
    id: "yapling", name: "Yapling", types: ["viral"],
    evolvesTo: "yapstorm", evolveLevel: 16,
    hp: 40, atk: 48, def: 34, spd: 66, catch: 58, bounty: 18,
    moves: ["chirpFlood", "talkOver", "reedRattle", "encoreLoop"],
    blurb: "Answers the question nobody asked, then its follow-up. The reeds stopped taking notes.", cry: "yap-yap",
  },
  yapstorm: {
    id: "yapstorm", name: "Yapstorm", types: ["viral", "chaos"],
    hp: 68, atk: 78, def: 56, spd: 90, catch: 24, bounty: 40,
    moves: ["rantBurst", "talkOver", "nestRiot", "encoreLoop"],
    moveUpgrades: { chirpFlood: "rantBurst", reedRattle: "nestRiot" },
    blurb: "One hedge, eleven mouths, zero listeners. Quorum reached. Minutes: noise.", cry: "YAP-YAP",
  },
  lurkit: {
    id: "lurkit", name: "Lurkit", types: ["cope", "signal"],
    evolvesTo: "threadmaw", evolveLevel: 18,
    hp: 46, atk: 38, def: 52, spd: 44, catch: 56, bounty: 18,
    moves: ["quietRead", "tabHoard", "sideGlance", "footnote"],
    blurb: "Reads every leaf in the thread. Posts nothing. Keeps the receipts for all of it.", cry: "...",
  },
  threadmaw: {
    id: "threadmaw", name: "Threadmaw", types: ["cope", "signal"],
    hp: 80, atk: 72, def: 78, spd: 58, catch: 22, bounty: 42,
    moves: ["novelDump", "tabHoard", "sideGlance", "marginBite"],
    moveUpgrades: { quietRead: "novelDump", footnote: "marginBite" },
    blurb: "Spoke once, at length. The reply outlasted the route. There are footnotes.", cry: "ahem",
  },
  ratiofin: {
    id: "ratiofin", name: "Ratiofin", types: ["signal", "chaos"],
    evolvesTo: "ratioking", evolveLevel: 17,
    hp: 42, atk: 54, def: 36, spd: 64, catch: 56, bounty: 20,
    moves: ["replySnap", "quotePin", "dunkSplash", "lPlus"],
    blurb: "A minnow the size of a bad take. First word: L. Second word also L.", cry: "ell",
  },
  ratioking: {
    id: "ratioking", name: "Ratioking", types: ["signal", "chaos"],
    hp: 70, atk: 84, def: 58, spd: 82, catch: 22, bounty: 44,
    moves: ["ratioBomb", "quotePin", "threadFlood", "lPlus"],
    moveUpgrades: { replySnap: "ratioBomb", dunkSplash: "threadFlood" },
    blurb: "Crowned by the replies, not the point. The pond voted. The pond was wrong.", cry: "L-L-L",
  },
  scrollmite: {
    id: "scrollmite", name: "Scrollmite", types: ["chaos", "cope"],
    evolvesTo: "doomwyrm", evolveLevel: 20,
    hp: 44, atk: 56, def: 38, spd: 58, catch: 50, bounty: 22,
    moves: ["flickDown", "keepReading", "tabSip", "doomFlick"],
    blurb: "Thumb first, morning never. Breakfast is a headline from Tuesday. Cold.", cry: "flick",
  },
  doomwyrm: {
    id: "doomwyrm", name: "Doomwyrm", types: ["chaos", "cope"],
    hp: 78, atk: 86, def: 64, spd: 74, catch: 20, bounty: 46,
    moves: ["bingeSwipe", "keepReading", "tabSip", "nightFeed"],
    moveUpgrades: { flickDown: "bingeSwipe", doomFlick: "nightFeed" },
    blurb: "Four hours of bad news, grown legs. Says one more. Means the whole night.", cry: "one-more",
  },
  finekit: {
    id: "finekit", name: "Finekit", types: ["flex", "cope"],
    evolvesTo: "kilnmutt", evolveLevel: 17,
    hp: 54, atk: 44, def: 58, spd: 32, catch: 54, bounty: 20,
    moves: ["politeNod", "kettleSit", "fineThanks", "steamHuff"],
    blurb: "Sits inside the kettle. Rates the fire polite. Declines to discuss the smoke.", cry: "fine",
  },
  kilnmutt: {
    id: "kilnmutt", name: "Kilnmutt", types: ["flex", "cope"],
    hp: 86, atk: 70, def: 88, spd: 40, catch: 22, bounty: 44,
    moves: ["stillFine", "kettleSit", "fineThanks", "hearthRoar"],
    moveUpgrades: { politeNod: "stillFine", steamHuff: "hearthRoar" },
    blurb: "The kettle became a helmet. The room is on fire. The room is fine, thanks.", cry: "all-fine",
  },
  npcat: {
    id: "npcat", name: "Npcat", types: ["signal", "cope"],
    evolvesTo: "lorelord", evolveLevel: 19,
    hp: 48, atk: 42, def: 50, spd: 46, catch: 55, bounty: 18,
    moves: ["blankStare", "scriptLine", "idleLoop", "copyPaste"],
    blurb: "One face, one nod, one line of dialogue. Ask twice and get the line twice.", cry: "mrrp",
  },
  lorelord: {
    id: "lorelord", name: "Lorelord", types: ["signal", "flex"],
    hp: 76, atk: 74, def: 76, spd: 62, catch: 22, bounty: 44,
    moves: ["faqDump", "blankStare", "idleLoop", "canonDrop"],
    moveUpgrades: { scriptLine: "faqDump", copyPaste: "canonDrop" },
    blurb: "The FAQ grew legs and a very patient stare. You were on page two. You still are.", cry: "see-page",
  },
  gatekit: {
    id: "gatekit", name: "Gatekit", types: ["chaos", "signal"],
    evolvesTo: "gatewyrm", evolveLevel: 16,
    hp: 42, atk: 50, def: 40, spd: 58, catch: 56, bounty: 18,
    moves: ["latchClick", "guestList", "fenceHop", "keepOut"],
    blurb: "Carries its own fence. The guest list is blank. It checks the blank list anyway.", cry: "who-goes",
  },
  gatewyrm: {
    id: "gatewyrm", name: "Gatewyrm", types: ["chaos", "signal"],
    hp: 72, atk: 80, def: 68, spd: 76, catch: 24, bounty: 42,
    moves: ["barSlam", "guestList", "yardShut", "keepOut"],
    moveUpgrades: { latchClick: "barSlam", fenceHop: "yardShut" },
    blurb: "The fence stood up and took a clipboard. It still wants your name. Spell it.", cry: "closed",
  },
  glazeling: {
    id: "glazeling", name: "Glazeling", types: ["flex"],
    evolvesTo: "glazewyrm", evolveLevel: 18,
    hp: 48, atk: 40, def: 46, spd: 42, catch: 50, bounty: 20,
    moves: ["softBuff", "highShine", "polishPuff", "yesAnd"],
    blurb: "Praises the puddle until the puddle believes it. The puddle is still a puddle.", cry: "nice",
  },
  glazewyrm: {
    id: "glazewyrm", name: "Glazewyrm", types: ["flex", "viral"],
    hp: 80, atk: 74, def: 72, spd: 60, catch: 22, bounty: 44,
    moves: ["fullGlaze", "highShine", "compliment", "yesAnd"],
    moveUpgrades: { softBuff: "fullGlaze", polishPuff: "compliment" },
    blurb: "Polished the lane to a shine. Nobody can find the dirt. Nobody looks anymore.", cry: "stunning",
  },
  capling: {
    id: "capling", name: "Capling", types: ["viral", "chaos"],
    evolvesTo: "nocapra", evolveLevel: 16,
    hp: 44, atk: 48, def: 38, spd: 60, catch: 62, bounty: 16,
    moves: ["hatTip", "fibSpin", "tallTale", "peakCap"],
    blurb: "A hat with a witness attached. Half the stories are the hat's. The good half.", cry: "cap",
  },
  nocapra: {
    id: "nocapra", name: "Nocapra", types: ["viral", "flex"],
    hp: 74, atk: 82, def: 64, spd: 78, catch: 24, bounty: 42,
    moves: ["truthKick", "fibSpin", "stampede", "peakCap"],
    moveUpgrades: { hatTip: "truthKick", tallTale: "stampede" },
    blurb: "Kicked off the hat. Kept the ledger. Every tall tale now has a hoofprint.", cry: "no-cap",
  },
  grassnudge: {
    id: "grassnudge", name: "Grassnudge", types: ["viral", "flex"],
    evolvesTo: "lawnlord", evolveLevel: 17,
    hp: 46, atk: 44, def: 48, spd: 52, catch: 58, bounty: 18,
    moves: ["turfTap", "goOutside", "seedToss", "sitLawn"],
    blurb: "A tuft of lawn that taps your boot doorward. Cannot read. Has views on screens.", cry: "out",
  },
  lawnlord: {
    id: "lawnlord", name: "Lawnlord", types: ["viral", "flex"],
    hp: 84, atk: 76, def: 80, spd: 54, catch: 22, bounty: 44,
    moves: ["lawnSweep", "goOutside", "parkDecree", "sitLawn"],
    moveUpgrades: { turfTap: "lawnSweep", seedToss: "parkDecree" },
    blurb: "The park elected a steward. Its one policy is outside. Appeals are heard outside.", cry: "touch",
  },
  seethtoad: {
    id: "seethtoad", name: "Seethtoad", types: ["cope", "chaos"],
    evolvesTo: "maldmaw", evolveLevel: 18,
    hp: 50, atk: 46, def: 44, spd: 40, catch: 58, bounty: 18,
    moves: ["simmer", "steamPop", "holdIt", "maldSip"],
    blurb: "A toad at a rolling boil. The lid rattles when somebody is wrong. It rattles.", cry: "hrm",
  },
  maldmaw: {
    id: "maldmaw", name: "Maldmaw", types: ["cope", "chaos"],
    hp: 82, atk: 78, def: 70, spd: 52, catch: 22, bounty: 44,
    moves: ["fullSeethe", "lidBlow", "holdIt", "maldSip"],
    moveUpgrades: { simmer: "fullSeethe", steamPop: "lidBlow" },
    blurb: "The lid left. The argument stayed. It has been Tuesday in there for weeks.", cry: "MALD",
  },
  deluling: {
    id: "deluling", name: "Deluling", types: ["cope", "signal"],
    evolvesTo: "delulord", evolveLevel: 19,
    hp: 46, atk: 42, def: 48, spd: 50, catch: 56, bounty: 18,
    moves: ["roseLens", "maybeSo", "daydream", "plotArmor"],
    blurb: "Rose lenses, one running plot. The puddle is a castle if you squint. It squints.", cry: "what-if",
  },
  delulord: {
    id: "delulord", name: "Delulord", types: ["cope", "flex"],
    hp: 78, atk: 72, def: 76, spd: 62, catch: 22, bounty: 44,
    moves: ["canonWish", "roseLens", "mainCharacter", "plotArmor"],
    moveUpgrades: { maybeSo: "canonWish", daydream: "mainCharacter" },
    blurb: "The plot got a title page and a sequel. You are in it. You are the setback.", cry: "destined",
  },
  vibeling: {
    id: "vibeling", name: "Vibeling", types: ["viral", "flex"],
    evolvesTo: "vibewyrm", evolveLevel: 16,
    hp: 42, atk: 46, def: 38, spd: 60, catch: 58, bounty: 18,
    moves: ["vibeCheck", "moodWave", "nodOff", "bouncePass"],
    blurb: "A pocket sun in borrowed shades. Rates the puddle before hopping in. Rates itself.", cry: "vibe",
  },
  vibewyrm: {
    id: "vibewyrm", name: "Vibewyrm", types: ["viral", "flex"],
    hp: 74, atk: 80, def: 62, spd: 84, catch: 24, bounty: 42,
    moves: ["vibeSweep", "moodWave", "nodOff", "crowdSurge"],
    moveUpgrades: { vibeCheck: "vibeSweep", bouncePass: "crowdSurge" },
    blurb: "Never took the shades off. The room agrees with them now. The room was asked once.", cry: "checked",
  },
  draftkit: {
    id: "draftkit", name: "Draftkit", types: ["cope", "signal"],
    evolvesTo: "postwyrm", evolveLevel: 17,
    hp: 46, atk: 42, def: 48, spd: 48, catch: 56, bounty: 18,
    moves: ["unsent", "scratchNote", "saveDraft", "marginTap"],
    blurb: "A note folded past sending. The crease is load-bearing. The words are not.", cry: "dot-dot",
  },
  postwyrm: {
    id: "postwyrm", name: "Postwyrm", types: ["cope", "signal"],
    hp: 78, atk: 76, def: 74, spd: 64, catch: 22, bounty: 44,
    moves: ["unsent", "threadPost", "saveDraft", "replyStorm"],
    moveUpgrades: { scratchNote: "threadPost", marginTap: "replyStorm" },
    blurb: "Grew a spine and a publish button. Pressed it. Has not slept since.", cry: "posted",
  },
  spoilpup: {
    id: "spoilpup", name: "Spoilpup", types: ["chaos", "signal"],
    evolvesTo: "leakmaw", evolveLevel: 18,
    hp: 44, atk: 52, def: 36, spd: 58, catch: 52, bounty: 20,
    moves: ["peekAhead", "leakDrop", "hushBark", "endingHint"],
    blurb: "Cone ears, no volume knob. Tells you the last page while you shelve the book.", cry: "psst",
  },
  leakmaw: {
    id: "leakmaw", name: "Leakmaw", types: ["chaos", "signal"],
    hp: 76, atk: 86, def: 60, spd: 78, catch: 22, bounty: 46,
    moves: ["peekAhead", "plotDump", "spoilerHowl", "endingHint"],
    moveUpgrades: { leakDrop: "plotDump", hushBark: "spoilerHowl" },
    blurb: "The cone became a megaphone. The ending is already out. So is the sequel's.", cry: "LEAK",
  },
  queueling: {
    id: "queueling", name: "Queueling", types: ["cope", "liquid"],
    evolvesTo: "linebaron", evolveLevel: 16,
    hp: 50, atk: 40, def: 52, spd: 36, catch: 60, bounty: 16,
    moves: ["takeANumber", "waitDrip", "shuffleUp", "holdPlace"],
    blurb: "A ticket with feet, holding number seven. No window has ever opened. It waits.", cry: "next",
  },
  linebaron: {
    id: "linebaron", name: "Linebaron", types: ["cope", "liquid"],
    hp: 86, atk: 68, def: 84, spd: 44, catch: 22, bounty: 42,
    moves: ["takeANumber", "counterCall", "rushLane", "holdPlace"],
    moveUpgrades: { waitDrip: "counterCall", shuffleUp: "rushLane" },
    blurb: "Got the bell. Still no window. Rings anyway; the line believes in the bell.", cry: "NOW",
  },
  receiptling: {
    id: "receiptling", name: "Receiptling", types: ["signal", "flex"],
    evolvesTo: "auditlord", evolveLevel: 18,
    hp: 48, atk: 50, def: 46, spd: 44, catch: 50, bounty: 20,
    moves: ["paperClip", "inkStamp", "fileAway", "showProof"],
    blurb: "A walking slip. Itemizes the joke, the delivery, and the pause before it.", cry: "see-here",
  },
  auditlord: {
    id: "auditlord", name: "Auditlord", types: ["signal", "flex"],
    hp: 80, atk: 82, def: 78, spd: 58, catch: 22, bounty: 46,
    moves: ["auditSlam", "ledgerBeam", "fileAway", "showProof"],
    moveUpgrades: { paperClip: "auditSlam", inkStamp: "ledgerBeam" },
    blurb: "The slip became a ledger. The timestamp is the punchline. Nobody laughs on time.", cry: "filed",
  },
  clipfinch: {
    id: "clipfinch", name: "Clipfinch", types: ["viral", "chaos"],
    evolvesTo: "cliplord", evolveLevel: 19,
    hp: 40, atk: 50, def: 34, spd: 64, catch: 54, bounty: 20,
    moves: ["snipCut", "contextToss", "outOfFrame", "loopClip"],
    blurb: "Carries two seconds of film. The setup was in the other two. It sold those.", cry: "snip",
  },
  cliplord: {
    id: "cliplord", name: "Cliplord", types: ["viral", "chaos"],
    hp: 72, atk: 84, def: 58, spd: 86, catch: 22, bounty: 46,
    moves: ["viralCut", "montage", "outOfFrame", "loopClip"],
    moveUpgrades: { snipCut: "viralCut", contextToss: "montage" },
    blurb: "The clip is the whole show now. Context auditioned. Context did not make the cut.", cry: "CLIP",
  },
  toquelin: {
    id: "toquelin", name: "Toquelin", types: ["flex", "cope"],
    evolvesTo: "tuquelord", evolveLevel: 16,
    hp: 44, atk: 42, def: 48, spd: 50, catch: 58, bounty: 18,
    moves: ["brimTip", "snowPuff", "earFlap", "stayWarm"],
    blurb: "A tuque that walks. The pompom is load-bearing. There is no head under there.", cry: "tuque",
  },
  tuquelord: {
    id: "tuquelord", name: "Tuquelord", types: ["flex", "cope"],
    hp: 82, atk: 74, def: 80, spd: 56, catch: 24, bounty: 42,
    moves: ["tuqueSlam", "blizzardNod", "earFlap", "stayWarm"],
    moveUpgrades: { brimTip: "tuqueSlam", snowPuff: "blizzardNod" },
    blurb: "The tuque annexed a scarf. It comes off in June. June has not been scheduled.", cry: "keep",
  },
  poutkit: {
    id: "poutkit", name: "Poutkit", types: ["liquid", "flex"],
    evolvesTo: "gravylord", evolveLevel: 17,
    hp: 50, atk: 44, def: 46, spd: 40, catch: 56, bounty: 18,
    moves: ["curdToss", "gravyDip", "cheesePull", "napkinWrap"],
    blurb: "A fry boat with a gravy personality. Squeaks under pressure. Curds do that.", cry: "squeak",
  },
  gravylord: {
    id: "gravylord", name: "Gravylord", types: ["liquid", "flex"],
    hp: 86, atk: 76, def: 78, spd: 48, catch: 22, bounty: 44,
    moves: ["gravyWave", "platterSlam", "cheesePull", "napkinWrap"],
    moveUpgrades: { curdToss: "gravyWave", gravyDip: "platterSlam" },
    blurb: "The boat made platter. The lane is lunch now. Bring your own napkin.", cry: "pour",
  },
  ghostkit: {
    id: "ghostkit", name: "Ghostkit", types: ["signal", "cope"],
    evolvesTo: "ghostwyrm", evolveLevel: 18,
    hp: 42, atk: 40, def: 44, spd: 58, catch: 54, bounty: 20,
    moves: ["leftOnRead", "fadeTap", "seenOnce", "muteChat"],
    blurb: "Opened the note. Read the note. Left. The tick stays grey on principle.", cry: "seen",
  },
  ghostwyrm: {
    id: "ghostwyrm", name: "Ghostwyrm", types: ["signal", "cope"],
    hp: 74, atk: 78, def: 70, spd: 84, catch: 22, bounty: 44,
    moves: ["leftOnRead", "unreadBomb", "vanishHowl", "muteChat"],
    moveUpgrades: { seenOnce: "unreadBomb", fadeTap: "vanishHowl" },
    blurb: "The bubble sprouted a tail and better excuses. Still grey. Still typing, allegedly.", cry: "gone",
  },
  crashpup: {
    id: "crashpup", name: "Crashpup", types: ["chaos", "viral"],
    evolvesTo: "crashmaw", evolveLevel: 18,
    hp: 46, atk: 54, def: 36, spd: 56, catch: 52, bounty: 20,
    moves: ["crashOut", "rantBite", "redThread", "takeFive"],
    blurb: "A pup on a thread. The thread is the leash. The leash is the argument. Walk it.", cry: "wait-",
  },
  crashmaw: {
    id: "crashmaw", name: "Crashmaw", types: ["chaos", "viral"],
    hp: 78, atk: 88, def: 58, spd: 76, catch: 22, bounty: 46,
    moves: ["fullCrash", "rantStorm", "redThread", "takeFive"],
    moveUpgrades: { crashOut: "fullCrash", rantBite: "rantStorm" },
    blurb: "The leash snapped at reply nine. The thread is the route now. Mind the tangle.", cry: "CRASH",
  },
  sauceling: {
    id: "sauceling", name: "Sauceling", types: ["flex", "chaos"],
    evolvesTo: "saucelord", evolveLevel: 16,
    hp: 44, atk: 48, def: 40, spd: 58, catch: 60, bounty: 16,
    moves: ["dropSauce", "sourceLink", "tasteTest", "bottleCap"],
    blurb: "A pocket bottle with the label scratched off. Will not name the recipe. Ever.", cry: "no-recipe",
  },
  saucelord: {
    id: "saucelord", name: "Saucelord", types: ["flex", "chaos"],
    hp: 76, atk: 84, def: 64, spd: 80, catch: 24, bounty: 42,
    moves: ["sauceFlood", "recipeSlam", "sourceLink", "bottleCap"],
    moveUpgrades: { dropSauce: "sauceFlood", tasteTest: "recipeSlam" },
    blurb: "Uncorked. Everyone on the route wants the source. The source is a wink.", cry: "drop",
  },
  pingling: {
    id: "pingling", name: "Pingling", types: ["viral", "signal"],
    evolvesTo: "pinglord", evolveLevel: 19,
    hp: 40, atk: 50, def: 36, spd: 62, catch: 50, bounty: 20,
    moves: ["pingPing", "badgePop", "muteSwipe", "quietHours"],
    blurb: "A badge with feet. One unread is already too many. It carries forty. It thrives.", cry: "ping",
  },
  pinglord: {
    id: "pinglord", name: "Pinglord", types: ["viral", "signal"],
    hp: 70, atk: 82, def: 56, spd: 88, catch: 22, bounty: 46,
    moves: ["pingStorm", "alertBlast", "badgePop", "quietHours"],
    moveUpgrades: { pingPing: "pingStorm", muteSwipe: "alertBlast" },
    blurb: "The badge stopped counting at 99. Quiet hours were repealed. You will hear about it.", cry: "PING",
  },
  slayling: {
    id: "slayling", name: "Slayling", types: ["viral", "flex"],
    evolvesTo: "slaylord", evolveLevel: 16,
    hp: 42, atk: 50, def: 38, spd: 60, catch: 58, bounty: 18,
    moves: ["slayPose", "lookBook", "walkOff", "rimLight"],
    blurb: "A pup with a runway and nowhere to be. The walk is the whole joke. It lands.", cry: "slay",
  },
  slaylord: {
    id: "slaylord", name: "Slaylord", types: ["viral", "flex"],
    hp: 72, atk: 84, def: 62, spd: 82, catch: 24, bounty: 42,
    moves: ["slayStorm", "coutureSlam", "walkOff", "rimLight"],
    moveUpgrades: { slayPose: "slayStorm", lookBook: "coutureSlam" },
    blurb: "The walk got a cape and a lighting budget. The lane is the show. You are seated.", cry: "SLAY",
  },
  cookling: {
    id: "cookling", name: "Cookling", types: ["flex", "chaos"],
    evolvesTo: "cookwyrm", evolveLevel: 16,
    hp: 48, atk: 46, def: 44, spd: 48, catch: 56, bounty: 18,
    moves: ["letCook", "spiceToss", "lowHeat", "lidOn"],
    blurb: "A pocket chef, permanently mid-simmer. Will not plate. Will not say what is in it.", cry: "not-yet",
  },
  cookwyrm: {
    id: "cookwyrm", name: "Cookwyrm", types: ["flex", "chaos"],
    hp: 82, atk: 80, def: 72, spd: 62, catch: 22, bounty: 44,
    moves: ["cookOff", "kitchenFire", "lowHeat", "lidOn"],
    moveUpgrades: { letCook: "cookOff", spiceToss: "kitchenFire" },
    blurb: "The kitchen finally opened. Let the whole route cook. The alarm is seasoning.", cry: "COOK",
  },
  midling: {
    id: "midling", name: "Midling", types: ["cope", "signal"],
    evolvesTo: "midbaron", evolveLevel: 17,
    hp: 50, atk: 40, def: 50, spd: 42, catch: 58, bounty: 16,
    moves: ["midTake", "nuanceDump", "bothSides", "stayMid"],
    blurb: "A take so mid it filed for a body. Agrees with both sides, sides with neither.", cry: "mid",
  },
  midbaron: {
    id: "midbaron", name: "Midbaron", types: ["cope", "signal"],
    hp: 86, atk: 70, def: 82, spd: 52, catch: 22, bounty: 42,
    moves: ["midwave", "centristSlam", "bothSides", "stayMid"],
    moveUpgrades: { midTake: "midwave", nuanceDump: "centristSlam" },
    blurb: "Both sides, neither side, one throne on the fence. The fence charges rent.", cry: "BOTH",
  },
  lockkit: {
    id: "lockkit", name: "Lockkit", types: ["signal", "flex"],
    evolvesTo: "locklord", evolveLevel: 18,
    hp: 44, atk: 52, def: 40, spd: 54, catch: 52, bounty: 20,
    moves: ["lockIn", "screenTime", "timeout", "focusMode"],
    blurb: "A padlock with feet. Screen time ended at nine. It is nine somewhere, always.", cry: "click",
  },
  locklord: {
    id: "locklord", name: "Locklord", types: ["signal", "flex"],
    hp: 76, atk: 86, def: 64, spd: 76, catch: 22, bounty: 46,
    moves: ["lockStorm", "overtime", "timeout", "focusMode"],
    moveUpgrades: { lockIn: "lockStorm", screenTime: "overtime" },
    blurb: "Locked in. Shackle shut, keyhole grinning. The timer does not have an off.", cry: "LOCK",
  },
  depkit: {
    id: "depkit", name: "Depkit", types: ["chaos", "liquid"],
    evolvesTo: "depbaron", evolveLevel: 17,
    hp: 46, atk: 48, def: 46, spd: 50, catch: 56, bounty: 18,
    moves: ["lateOpen", "slushToss", "lottoScratch", "afterHours"],
    blurb: "A dépanneur on four legs. Open late, closed on nuance. The slush is blue again.", cry: "still-open",
  },
  depbaron: {
    id: "depbaron", name: "Depbaron", types: ["chaos", "liquid"],
    hp: 80, atk: 82, def: 74, spd: 60, catch: 22, bounty: 44,
    moves: ["slushFlood", "cornerSlam", "lottoScratch", "afterHours"],
    moveUpgrades: { lateOpen: "slushFlood", slushToss: "cornerSlam" },
    blurb: "Slush machine for a throne. Sells you tomorrow at 2am. The lotto still loses.", cry: "OPEN",
  },
  ehcorn: {
    id: "ehcorn", name: "Ehcorn", types: ["flex", "viral"],
    evolvesTo: "ehvergreen", evolveLevel: 16,
    hp: 46, atk: 44, def: 46, spd: 48, catch: 60, bounty: 16,
    moves: ["politeTap", "ehEcho", "sorryBud", "budCoat"],
    blurb: "Apologizes to the boot that stepped on it. Then asks if the boot is alright. Eh?", cry: "eh?",
  },
  ehvergreen: {
    id: "ehvergreen", name: "Ehvergreen", types: ["flex", "viral"],
    hp: 80, atk: 74, def: 78, spd: 60, catch: 24, bounty: 42,
    moves: ["canopyClap", "chorusEh", "sorryBud", "budCoat"],
    moveUpgrades: { politeTap: "canopyClap", ehEcho: "chorusEh" },
    blurb: "Every branch has a reply. Every reply ends in eh. The canopy calls that consensus.", cry: "EH!",
  },
  snoozlet: {
    id: "snoozlet", name: "Snoozlet", types: ["cope", "chaos"],
    evolvesTo: "napocalypse", evolveLevel: 17,
    hp: 52, atk: 40, def: 48, spd: 34, catch: 58, bounty: 18,
    moves: ["pillowBop", "yawnPuff", "snoozeButton", "blanketFort"],
    blurb: "A pillow moth running on one hour and a brave little yawn. The alarm lost.", cry: "eepy",
  },
  napocalypse: {
    id: "napocalypse", name: "Napocalypse", types: ["cope", "chaos"],
    hp: 88, atk: 74, def: 78, spd: 44, catch: 22, bounty: 44,
    moves: ["napquake", "dreamSpill", "snoozeButton", "blanketFort"],
    moveUpgrades: { pillowBop: "napquake", yawnPuff: "dreamSpill" },
    blurb: "Five more minutes, filed as a weather warning. The duvet has wings and a mandate.", cry: "zz-ZOOM",
  },
  coneling: {
    id: "coneling", name: "Coneling", types: ["chaos", "flex"],
    evolvesTo: "detourant", evolveLevel: 18,
    hp: 44, atk: 50, def: 46, spd: 46, catch: 56, bounty: 18,
    moves: ["coneTap", "laneToss", "reroute", "workZone"],
    blurb: "A road cone with antennae. Just one more lane, it promises. Promised that in May.", cry: "merge",
  },
  detourant: {
    id: "detourant", name: "Detourant", types: ["chaos", "flex"],
    hp: 76, atk: 82, def: 78, spd: 62, catch: 22, bounty: 44,
    moves: ["detourRush", "laneClosure", "reroute", "workZone"],
    moveUpgrades: { coneTap: "detourRush", laneToss: "laneClosure" },
    blurb: "A barricade with six legs. Its shortcut circles three blocks, then itself. Twice.", cry: "DETOUR",
  },
  syrupup: {
    id: "syrupup", name: "Syrupup", types: ["liquid", "viral"],
    evolvesTo: "sapreme", evolveLevel: 17,
    hp: 48, atk: 46, def: 42, spd: 48, catch: 58, bounty: 18,
    moves: ["sapSlap", "mapleDrip", "stickyPaws", "sugarRest"],
    blurb: "A sap bucket with paws. The floor is sticky. The floor is supposed to be sticky.", cry: "drip-eh",
  },
  sapreme: {
    id: "sapreme", name: "Sapreme", types: ["liquid", "viral"],
    hp: 82, atk: 78, def: 70, spd: 64, catch: 22, bounty: 44,
    moves: ["syrupSurge", "sweetStamp", "stickyPaws", "sugarRest"],
    moveUpgrades: { sapSlap: "syrupSurge", mapleDrip: "sweetStamp" },
    blurb: "Sovereign of the sugar shack. All rise, if your boots will let you. They will not.", cry: "SAPREME",
  },
  bufferret: {
    id: "bufferret", name: "Bufferret", types: ["signal", "cope"],
    evolvesTo: "lagomorph", evolveLevel: 19,
    hp: 42, atk: 48, def: 38, spd: 60, catch: 54, bounty: 20,
    moves: ["bufferBite", "lagSpike", "pleaseWait", "cacheNap"],
    blurb: "Chases its own loading ring. Ninety-nine percent. Has been for two seasons.", cry: "buff...",
  },
  lagomorph: {
    id: "lagomorph", name: "Lagomorph", types: ["signal", "cope"],
    hp: 74, atk: 80, def: 62, spd: 84, catch: 22, bounty: 46,
    moves: ["packetBurst", "loadingLoop", "pleaseWait", "cacheNap"],
    moveUpgrades: { bufferBite: "packetBurst", lagSpike: "loadingLoop" },
    blurb: "The ears loaded first. The rabbit arrived later, all at once, out of order.", cry: "...GO",
  },
  honklet: {
    id: "honklet", name: "Honklet", types: ["liquid", "signal"],
    evolvesTo: "honkference", evolveLevel: 16,
    hp: 48, atk: 44, def: 44, spd: 48, catch: 60, bounty: 18,
    moves: ["honkTap", "voiceNote", "micCheck", "muteThread"],
    blurb: "Sends voice notes from the reeds. Every note is honk. Every note is four minutes.", cry: "honk?",
  },
  honkference: {
    id: "honkference", name: "Honkference", types: ["liquid", "signal"],
    hp: 82, atk: 76, def: 72, spd: 64, catch: 22, bounty: 44,
    moves: ["honkCast", "pondOvation", "micCheck", "muteThread"],
    moveUpgrades: { honkTap: "honkCast", voiceNote: "pondOvation" },
    blurb: "Called a meeting. Chairs it, minutes it, speaks at it. Mute was never installed.", cry: "HONK ALL",
  },
  chillip: {
    id: "chillip", name: "Chillip", types: ["cope", "liquid"],
    evolvesTo: "chillossus", evolveLevel: 17,
    hp: 52, atk: 40, def: 48, spd: 36, catch: 58, bounty: 18,
    moves: ["chillNip", "frostSip", "touchSnow", "teaBreak"],
    blurb: "An ice-cube seal. Tells you to touch snow. Says it from inside a warm thermos.", cry: "chill",
  },
  chillossus: {
    id: "chillossus", name: "Chillossus", types: ["cope", "liquid"],
    hp: 88, atk: 72, def: 80, spd: 46, catch: 22, bounty: 44,
    moves: ["chillFront", "icebreaker", "touchSnow", "teaBreak"],
    moveUpgrades: { chillNip: "chillFront", frostSip: "icebreaker" },
    blurb: "A walking snow day with a tea lid. The forecast says relax. The school board agreed.", cry: "BRRR-EAK",
  },
  crumbit: {
    id: "crumbit", name: "Crumbit", types: ["flex", "viral"],
    evolvesTo: "breadwinner", evolveLevel: 17,
    hp: 44, atk: 48, def: 42, spd: 52, catch: 58, bounty: 18,
    moves: ["crumbPeck", "bagelSkip", "proofRest", "seededCoat"],
    blurb: "A bagel pigeon on the crumb grind. Calls the park bench an office. Files nothing.", cry: "crumb",
  },
  breadwinner: {
    id: "breadwinner", name: "Breadwinner", types: ["flex", "viral"],
    hp: 76, atk: 82, def: 68, spd: 76, catch: 22, bounty: 44,
    moves: ["breadQuake", "bakerDozen", "proofRest", "seededCoat"],
    moveUpgrades: { crumbPeck: "breadQuake", bagelSkip: "bakerDozen" },
    blurb: "The bread came home in a sesame cape. Meetings are toasted. Lunch is a mandate.", cry: "GET BREAD",
  },
  puckup: {
    id: "puckup", name: "Puckup", types: ["flex", "chaos"],
    evolvesTo: "clapback", evolveLevel: 18,
    hp: 46, atk: 50, def: 46, spd: 42, catch: 56, bounty: 18,
    moves: ["puckTap", "rinkRattle", "benchBreath", "chirpCheck"],
    blurb: "A puck crab chirping from the snowbank. Just a friendly pickup game. Bring a cage.", cry: "puck?",
  },
  clapback: {
    id: "clapback", name: "Clapback", types: ["flex", "chaos"],
    hp: 80, atk: 84, def: 78, spd: 56, catch: 22, bounty: 44,
    moves: ["clapShot", "boardCrash", "benchBreath", "chirpCheck"],
    moveUpgrades: { puckTap: "clapShot", rinkRattle: "boardCrash" },
    blurb: "Rink-armoured. Every reply comes off the boards at the height of your teeth.", cry: "CLAP!",
  },
  tabbit: {
    id: "tabbit", name: "Tabbit", types: ["signal", "chaos"],
    evolvesTo: "tabalanche", evolveLevel: 19,
    hp: 42, atk: 48, def: 38, spd: 60, catch: 54, bounty: 18,
    moves: ["tabTug", "linkFling", "closeTabs", "pinTab"],
    blurb: "A paper-tab rabbit. Every ear opens one more thing to read later. Later is full.", cry: "one more",
  },
  tabalanche: {
    id: "tabalanche", name: "Tabalanche", types: ["signal", "chaos"],
    hp: 74, atk: 80, def: 64, spd: 84, catch: 22, bounty: 44,
    moves: ["tabCascade", "bookmarkFall", "closeTabs", "pinTab"],
    moveUpgrades: { tabTug: "tabCascade", linkFling: "bookmarkFall" },
    blurb: "The tabs became a snowdrift. The original task is under there. It was due Friday.", cry: "ALL TABS",
  },
  frostbag: {
    id: "frostbag",
    evolvesTo: "glacierbag",
    evolveLevel: 30,
    name: "Frostbag",
    types: ["hodl"],
    hp: 50,
    atk: 48,
    def: 58,
    spd: 40,
    catch: 45,
    bounty: 24,
    moves: ["frostNibble", "diamondGrip", "snowballEffect", "coldStorage"],
    learnset: [{ level: 1, id: "frostNibble" }, { level: 1, id: "diamondGrip" }, { level: 18, id: "snowballEffect" }, { level: 22, id: "coldStorage" }, { level: 27, id: "neverSell" }],
    blurb: "A money sack that froze solid mid-panic. It will not open. It will not sell.",
    cry: "CLINK",
  },
  glacierbag: {
    id: "glacierbag",
    name: "Glacierbag",
    types: ["hodl", "flex"],
    hp: 78,
    atk: 74,
    def: 92,
    spd: 52,
    catch: 18,
    bounty: 48,
    moves: ["neverSell", "diamondGrip", "glacierBeam", "coldStorage"],
    moveUpgrades: { frostNibble: "neverSell", snowballEffect: "glacierBeam" },
    blurb: "Heavier every winter. Its seams hum with coins it swore never to spend.",
    cry: "KRRNCH",
  },
  pengwallet: {
    id: "pengwallet",
    evolvesTo: "coldvault",
    evolveLevel: 32,
    name: "Pengwallet",
    types: ["hodl", "signal"],
    hp: 48,
    atk: 46,
    def: 52,
    spd: 58,
    catch: 45,
    bounty: 24,
    moves: ["frostNibble", "pingBurst", "frozenOrder", "diamondGrip"],
    learnset: [{ level: 1, id: "frostNibble" }, { level: 1, id: "pingBurst" }, { level: 18, id: "frozenOrder" }, { level: 22, id: "diamondGrip" }, { level: 28, id: "coldStorage" }],
    blurb: "Waddles offline on purpose. The keypad on its belly only accepts compliments.",
    cry: "pip-beep",
  },
  coldvault: {
    id: "coldvault",
    name: "Coldvault",
    types: ["hodl", "signal"],
    hp: 74,
    atk: 78,
    def: 86,
    spd: 70,
    catch: 18,
    bounty: 48,
    moves: ["glacierBeam", "oracleBeam", "frozenOrder", "coldStorage"],
    moveUpgrades: { frostNibble: "glacierBeam", pingBurst: "oracleBeam" },
    blurb: "An emperor of cold storage. Twelve secret words sleep under its feathers.",
    cry: "BEEP-HONK",
  },
  icepup: {
    id: "icepup",
    evolvesTo: "diamondpaws",
    evolveLevel: 31,
    name: "Icepup",
    types: ["hodl"],
    hp: 46,
    atk: 56,
    def: 42,
    spd: 62,
    catch: 45,
    bounty: 22,
    moves: ["frostNibble", "quickFlip", "satHail", "frozenAssets"],
    learnset: [{ level: 1, id: "frostNibble" }, { level: 1, id: "quickFlip" }, { level: 18, id: "satHail" }, { level: 22, id: "frozenAssets" }, { level: 26, id: "neverSell" }],
    blurb: "Chews icicles for fun. Never lets go of a stick, a bone or a bad position.",
    cry: "yip!",
  },
  diamondpaws: {
    id: "diamondpaws",
    name: "Diamondpaws",
    types: ["hodl", "flex"],
    hp: 72,
    atk: 90,
    def: 66,
    spd: 84,
    catch: 18,
    bounty: 50,
    moves: ["permafrost", "goldChain", "satHail", "frozenAssets"],
    moveUpgrades: { frostNibble: "permafrost", quickFlip: "goldChain" },
    blurb: "Its paws turned to diamond from gripping too hard. It is very proud of this.",
    cry: "AWOOO",
  },
  candlecub: {
    id: "candlecub",
    evolvesTo: "wickwolf",
    evolveLevel: 30,
    name: "Candlecub",
    types: ["burn"],
    hp: 44,
    atk: 58,
    def: 40,
    spd: 64,
    catch: 45,
    bounty: 22,
    moves: ["emberTip", "gasSpike", "wickLash", "heatMap"],
    learnset: [{ level: 1, id: "emberTip" }, { level: 1, id: "gasSpike" }, { level: 18, id: "wickLash" }, { level: 22, id: "heatMap" }, { level: 26, id: "tokenBurn" }],
    blurb: "Its tail is a green candle. When it wags, somebody somewhere says wagmi.",
    cry: "fwip",
  },
  wickwolf: {
    id: "wickwolf",
    name: "Wickwolf",
    types: ["burn", "flex"],
    hp: 70,
    atk: 92,
    def: 60,
    spd: 88,
    catch: 18,
    bounty: 50,
    moves: ["greenCandle", "gasSpike", "flareUp", "heatMap"],
    moveUpgrades: { emberTip: "greenCandle", wickLash: "flareUp" },
    blurb: "Runs the dunes at night, a trail of wicks behind it like a live chart.",
    cry: "HOWWL",
  },
  shillpion: {
    id: "shillpion",
    evolvesTo: "shillscorp",
    evolveLevel: 31,
    name: "Shillpion",
    types: ["burn", "chaos"],
    hp: 46,
    atk: 60,
    def: 50,
    spd: 50,
    catch: 45,
    bounty: 24,
    moves: ["emberTip", "botBarrage", "ashCloud", "tokenBurn"],
    learnset: [{ level: 1, id: "emberTip" }, { level: 1, id: "botBarrage" }, { level: 18, id: "ashCloud" }, { level: 22, id: "tokenBurn" }, { level: 27, id: "flashCrash" }],
    blurb: "Its stinger is a megaphone. It only has one opinion, but it has it very loudly.",
    cry: "tk-tk",
  },
  shillscorp: {
    id: "shillscorp",
    name: "Shillscorp",
    types: ["burn", "chaos"],
    hp: 72,
    atk: 94,
    def: 80,
    spd: 64,
    catch: 18,
    bounty: 50,
    moves: ["marginCall", "botBarrage", "ashCloud", "tokenBurn"],
    moveUpgrades: { emberTip: "marginCall" },
    blurb: "Shills its own shell. Sold the dunes to three different buyers this week.",
    cry: "SKRRT",
  },
  sprikle: {
    id: "sprikle",
    evolvesTo: "saguarocket",
    evolveLevel: 30,
    name: "Sprikle",
    types: ["viral", "burn"],
    hp: 52,
    atk: 50,
    def: 56,
    spd: 42,
    catch: 45,
    bounty: 22,
    moves: ["hotTake", "emberTip", "retweetStorm", "heatMap"],
    learnset: [{ level: 1, id: "hotTake" }, { level: 1, id: "emberTip" }, { level: 18, id: "retweetStorm" }, { level: 22, id: "heatMap" }, { level: 25, id: "greenCandle" }],
    blurb: "A tiny cactus with a fuse. Every spine is a take, and every take is hot.",
    cry: "psst",
  },
  saguarocket: {
    id: "saguarocket",
    name: "Saguarocket",
    types: ["viral", "burn"],
    hp: 80,
    atk: 84,
    def: 78,
    spd: 56,
    catch: 18,
    bounty: 48,
    moves: ["trendingNow", "flareUp", "retweetStorm", "heatMap"],
    moveUpgrades: { hotTake: "trendingNow", emberTip: "flareUp" },
    blurb: "Launches at noon, lands by dusk, and posts about the trip in between.",
    cry: "WHOOOSH",
  },
  humpfund: {
    id: "humpfund",
    name: "Humpfund",
    types: ["flex", "hodl"],
    hp: 90,
    atk: 80,
    def: 78,
    spd: 52,
    catch: 15,
    bounty: 60,
    moves: ["goldChain", "diamondGrip", "neverSell", "quickFlip"],
    blurb: "A caravan camel with two humps: one for savings, one for more savings.",
    cry: "HRRUMPH",
  },
  emberbit: {
    id: "emberbit",
    evolvesTo: "blazebit",
    evolveLevel: 24,
    name: "Emberbit",
    types: ["burn"],
    hp: 40,
    atk: 52,
    def: 42,
    spd: 66,
    catch: 45,
    bounty: 20,
    moves: ["emberTip", "gasSpike", "wickLash", "heatMap"],
    learnset: [{ level: 1, id: "emberTip" }, { level: 1, id: "gasSpike" }, { level: 18, id: "wickLash" }, { level: 22, id: "heatMap" }, { level: 22, id: "tokenBurn" }],
    blurb: "A spark that fell off a burning coin and decided to keep going.",
    cry: "tsss",
  },
  blazebit: {
    id: "blazebit",
    evolvesTo: "infernomint",
    evolveLevel: 36,
    name: "Blazebit",
    types: ["burn"],
    hp: 60,
    atk: 74,
    def: 58,
    spd: 82,
    catch: 30,
    bounty: 34,
    moves: ["tokenBurn", "gasSpike", "wickLash", "heatMap"],
    learnset: [{ level: 1, id: "tokenBurn" }, { level: 1, id: "gasSpike" }, { level: 1, id: "wickLash" }, { level: 1, id: "heatMap" }, { level: 33, id: "marginCall" }],
    moveUpgrades: { emberTip: "tokenBurn" },
    blurb: "Half the supply of what it was. Twice as hot about it.",
    cry: "FSSHH",
  },
  infernomint: {
    id: "infernomint",
    name: "Infernomint",
    types: ["burn", "chaos"],
    hp: 82,
    atk: 98,
    def: 74,
    spd: 94,
    catch: 12,
    bounty: 62,
    moves: ["flareUp", "gasSpike", "blackSwan", "heatMap"],
    moveUpgrades: { tokenBurn: "flareUp", wickLash: "blackSwan" },
    blurb: "Mints fire and burns it in the same breath. Nobody has ever seen its supply.",
    cry: "BRRROOM",
  },
  gasbull: {
    id: "gasbull",
    evolvesTo: "magmabull",
    evolveLevel: 32,
    name: "Gasbull",
    types: ["burn", "flex"],
    hp: 58,
    atk: 62,
    def: 56,
    spd: 40,
    catch: 45,
    bounty: 24,
    moves: ["emberTip", "flexFlurry", "tokenBurn", "heatMap"],
    learnset: [{ level: 1, id: "emberTip" }, { level: 1, id: "flexFlurry" }, { level: 18, id: "tokenBurn" }, { level: 22, id: "heatMap" }, { level: 28, id: "marginCall" }],
    blurb: "Snorts gas fees. The higher the network cost, the harder it charges.",
    cry: "MOO-FF",
  },
  magmabull: {
    id: "magmabull",
    name: "Magmabull",
    types: ["burn", "flex"],
    hp: 88,
    atk: 96,
    def: 84,
    spd: 56,
    catch: 18,
    bounty: 52,
    moves: ["marginCall", "goldChain", "tokenBurn", "heatMap"],
    moveUpgrades: { emberTip: "marginCall", flexFlurry: "goldChain" },
    blurb: "Lava runs in the cracks of its hide. It only knows one direction: up.",
    cry: "BWAAARR",
  },
  moltwisp: {
    id: "moltwisp",
    name: "Moltwisp",
    types: ["burn", "rug"],
    hp: 70,
    atk: 88,
    def: 64,
    spd: 92,
    catch: 12,
    bounty: 58,
    moves: ["flareUp", "exitScam", "walletDrainer", "ashCloud"],
    blurb: "A ghost flame over the magma vents. It borrows warmth and never pays it back.",
    cry: "hoo-ff",
  },
  ruglet: {
    id: "ruglet",
    evolvesTo: "rugwraith",
    evolveLevel: 30,
    name: "Ruglet",
    types: ["rug"],
    hp: 44,
    atk: 50,
    def: 44,
    spd: 62,
    catch: 45,
    bounty: 22,
    moves: ["exitScam", "honeypot", "shadowFork", "fudCloud"],
    learnset: [{ level: 1, id: "exitScam" }, { level: 1, id: "honeypot" }, { level: 18, id: "shadowFork" }, { level: 22, id: "fudCloud" }, { level: 26, id: "rugPull" }],
    blurb: "A little floating carpet. Step on it and the floor becomes a very long story.",
    cry: "fwoop",
  },
  rugwraith: {
    id: "rugwraith",
    name: "Rugwraith",
    types: ["rug", "chaos"],
    hp: 70,
    atk: 90,
    def: 64,
    spd: 90,
    catch: 18,
    bounty: 50,
    moves: ["exitScam", "honeypot", "phantomMint", "delisting"],
    moveUpgrades: { shadowFork: "phantomMint", fudCloud: "delisting" },
    blurb: "Unrolls over the bog at night. Whatever it covers is simply gone by morning.",
    cry: "WHOOOSH",
  },
  mirebot: {
    id: "mirebot",
    evolvesTo: "mireshill",
    evolveLevel: 31,
    name: "Mirebot",
    types: ["rug", "liquid"],
    hp: 54,
    atk: 50,
    def: 56,
    spd: 40,
    catch: 45,
    bounty: 24,
    moves: ["dipRain", "honeypot", "walletDrainer", "darkPool"],
    learnset: [{ level: 1, id: "dipRain" }, { level: 1, id: "honeypot" }, { level: 18, id: "walletDrainer" }, { level: 22, id: "darkPool" }, { level: 27, id: "flashLoan" }],
    blurb: "A rusty trading bot that sank in the bog. It still promises ten percent daily.",
    cry: "bzzrt",
  },
  mireshill: {
    id: "mireshill",
    name: "Mireshill",
    types: ["rug", "liquid"],
    hp: 86,
    atk: 80,
    def: 86,
    spd: 56,
    catch: 18,
    bounty: 50,
    moves: ["slippageSurge", "honeypot", "walletDrainer", "darkPool"],
    moveUpgrades: { dipRain: "slippageSurge" },
    blurb: "Grew a swamp around its promises. The deeper you wade, the better the yield sounds.",
    cry: "GLRRBZT",
  },
  botfly: {
    id: "botfly",
    evolvesTo: "swarmlord",
    evolveLevel: 29,
    name: "Botfly",
    types: ["rug", "viral"],
    hp: 38,
    atk: 52,
    def: 34,
    spd: 76,
    catch: 45,
    bounty: 22,
    moves: ["botSwarm", "hotTake", "fudCloud", "exitScam"],
    learnset: [{ level: 1, id: "botSwarm" }, { level: 1, id: "hotTake" }, { level: 18, id: "fudCloud" }, { level: 22, id: "exitScam" }, { level: 25, id: "retweetStorm" }],
    blurb: "One gnat, forty accounts. It buzzes the same reply under every post.",
    cry: "bzz",
  },
  swarmlord: {
    id: "swarmlord",
    name: "Swarmlord",
    types: ["rug", "viral"],
    hp: 60,
    atk: 84,
    def: 54,
    spd: 104,
    catch: 18,
    bounty: 48,
    moves: ["botSwarm", "trendingNow", "fudCloud", "exitScam"],
    moveUpgrades: { hotTake: "trendingNow" },
    blurb: "A cloud of bots with one crown. Its engagement is enormous and entirely fake.",
    cry: "BZZZZZ",
  },
  honeytoad: {
    id: "honeytoad",
    evolvesTo: "honeybaron",
    evolveLevel: 32,
    name: "Honeytoad",
    types: ["rug", "cope"],
    hp: 56,
    atk: 48,
    def: 58,
    spd: 38,
    catch: 45,
    bounty: 24,
    moves: ["honeypot", "copiumJab", "shadowFork", "touchReality"],
    learnset: [{ level: 1, id: "honeypot" }, { level: 1, id: "copiumJab" }, { level: 18, id: "shadowFork" }, { level: 22, id: "touchReality" }, { level: 28, id: "sevenStages" }],
    blurb: "Keeps a jar of honey in its throat. You can buy in. You cannot get out.",
    cry: "glup",
  },
  honeybaron: {
    id: "honeybaron",
    name: "Honeybaron",
    types: ["rug", "cope"],
    hp: 88,
    atk: 76,
    def: 90,
    spd: 54,
    catch: 18,
    bounty: 50,
    moves: ["honeypot", "sevenStages", "delisting", "touchReality"],
    moveUpgrades: { copiumJab: "sevenStages", shadowFork: "delisting" },
    blurb: "Sits on a throne of sticky jars and calls every lost wallet a lesson.",
    cry: "GLOORP",
  },
  shardling: {
    id: "shardling",
    evolvesTo: "shardking",
    evolveLevel: 33,
    name: "Shardling",
    types: ["hodl", "signal"],
    hp: 46,
    atk: 48,
    def: 62,
    spd: 46,
    catch: 45,
    bounty: 24,
    moves: ["frostNibble", "pingBurst", "diamondGrip", "frozenOrder"],
    learnset: [{ level: 1, id: "frostNibble" }, { level: 1, id: "pingBurst" }, { level: 18, id: "diamondGrip" }, { level: 22, id: "frozenOrder" }, { level: 29, id: "coldStorage" }],
    blurb: "A crystal that grew around a lost seed phrase. It glows when you almost remember.",
    cry: "ting",
  },
  shardking: {
    id: "shardking",
    name: "Shardking",
    types: ["hodl", "signal"],
    hp: 72,
    atk: 80,
    def: 98,
    spd: 60,
    catch: 16,
    bounty: 52,
    moves: ["permafrost", "oracleBeam", "diamondGrip", "frozenOrder"],
    moveUpgrades: { frostNibble: "permafrost", pingBurst: "oracleBeam" },
    blurb: "Every facet stores one block. Its crown is the latest, and it never stops growing.",
    cry: "TIIING",
  },
  prismpup: {
    id: "prismpup",
    evolvesTo: "prismhound",
    evolveLevel: 30,
    name: "Prismpup",
    types: ["signal", "hodl"],
    hp: 44,
    atk: 56,
    def: 44,
    spd: 62,
    catch: 45,
    bounty: 22,
    moves: ["frontRun", "frostNibble", "pingBurst", "snowballEffect"],
    learnset: [{ level: 1, id: "frontRun" }, { level: 1, id: "frostNibble" }, { level: 18, id: "pingBurst" }, { level: 22, id: "snowballEffect" }, { level: 26, id: "frozenOrder" }],
    blurb: "Splits every beam of light into seven price feeds and chases all of them.",
    cry: "arf!",
  },
  prismhound: {
    id: "prismhound",
    name: "Prismhound",
    types: ["signal", "hodl"],
    hp: 70,
    atk: 88,
    def: 68,
    spd: 88,
    catch: 18,
    bounty: 50,
    moves: ["frontRun", "glacierBeam", "oracleBeam", "snowballEffect"],
    moveUpgrades: { frostNibble: "glacierBeam", pingBurst: "oracleBeam" },
    blurb: "Its coat bends the light of the cave into rainbows. It sees the order before you place it.",
    cry: "AROOO",
  },
  nodelet: {
    id: "nodelet",
    evolvesTo: "validrake",
    evolveLevel: 32,
    name: "Nodelet",
    types: ["signal", "rug"],
    hp: 48,
    atk: 54,
    def: 48,
    spd: 56,
    catch: 45,
    bounty: 24,
    moves: ["pingBurst", "darkPool", "exitScam", "frontRun"],
    learnset: [{ level: 1, id: "pingBurst" }, { level: 1, id: "darkPool" }, { level: 18, id: "exitScam" }, { level: 22, id: "frontRun" }, { level: 28, id: "shadowFork" }],
    blurb: "A floating node that validates anything for a small tip. It is very agreeable.",
    cry: "blip",
  },
  validrake: {
    id: "validrake",
    name: "Validrake",
    types: ["signal", "rug"],
    hp: 74,
    atk: 86,
    def: 74,
    spd: 80,
    catch: 18,
    bounty: 52,
    moves: ["oracleBeam", "darkPool", "phantomMint", "frontRun"],
    moveUpgrades: { pingBurst: "oracleBeam", exitScam: "phantomMint" },
    blurb: "A drake of linked nodes. It signs every block, including the ones it invented.",
    cry: "KRAAAK",
  },
  cachemole: {
    id: "cachemole",
    evolvesTo: "vaultmole",
    evolveLevel: 31,
    name: "Cachemole",
    types: ["hodl", "cope"],
    hp: 58,
    atk: 56,
    def: 60,
    spd: 32,
    catch: 45,
    bounty: 24,
    moves: ["frostNibble", "copiumJab", "diamondGrip", "sevenStages"],
    learnset: [{ level: 1, id: "frostNibble" }, { level: 1, id: "copiumJab" }, { level: 18, id: "diamondGrip" }, { level: 22, id: "sevenStages" }, { level: 27, id: "coldStorage" }],
    blurb: "Digs tunnels to hide its gems, then forgets where. Sad, but still holding.",
    cry: "snff",
  },
  vaultmole: {
    id: "vaultmole",
    name: "Vaultmole",
    types: ["hodl", "cope"],
    hp: 90,
    atk: 84,
    def: 92,
    spd: 44,
    catch: 18,
    bounty: 50,
    moves: ["neverSell", "copiumJab", "diamondGrip", "sevenStages"],
    moveUpgrades: { frostNibble: "neverSell" },
    blurb: "Its claws are vault doors. The treasure behind them is mostly emotional.",
    cry: "SNOORF",
  },
  genesiwyrm: {
    id: "genesiwyrm",
    name: "Genesiwyrm",
    types: ["hodl", "burn"],
    hp: 100,
    atk: 100,
    def: 100,
    spd: 90,
    catch: 8,
    bounty: 120,
    moves: ["glacierBeam", "flareUp", "coldStorage", "heatMap"],
    blurb: "Hatched from the first block ever mined. Half frost, half fire, fully on-chain.",
    cry: "GENNNESIS",
  },
  ursarug: {
    id: "ursarug",
    name: "Ursarug",
    types: ["rug", "hodl"],
    hp: 104,
    atk: 108,
    def: 92,
    spd: 76,
    catch: 8,
    bounty: 120,
    moves: ["delisting", "permafrost", "darkPool", "fudCloud"],
    blurb: "A bear market with fur. Wherever it sleeps, the floor quietly gives way.",
    cry: "GRRRUMBLE",
  },
  // ---- Undernet ------------------------------------------------------------------------------
  chatbit: {
    id: "chatbit",
    evolvesTo: "chatlord",
    evolveLevel: 34,
    name: "Chatbit",
    types: ["ai"],
    hp: 58,
    atk: 62,
    def: 60,
    spd: 74,
    catch: 40,
    bounty: 34,
    moves: ["promptJab", "autocomplete", "rateLimit", "captchaWall"],
    learnset: [{ level: 1, id: "promptJab" }, { level: 1, id: "autocomplete" }, { level: 30, id: "rateLimit" }, { level: 31, id: "captchaWall" }, { level: 33, id: "agentSwarm" }],
    blurb: "A speech bubble that learned to talk back. It is typing... it is always typing.",
    cry: "ding!",
  },
  chatlord: {
    id: "chatlord",
    name: "Chatlord",
    types: ["ai", "viral"],
    hp: 82,
    atk: 92,
    def: 80,
    spd: 96,
    catch: 20,
    bounty: 58,
    moves: ["agentSwarm", "autocomplete", "rateLimit", "hypeTrain"],
    moveUpgrades: { promptJab: "agentSwarm", captchaWall: "hypeTrain" },
    blurb: "Answers every thread at once. Somehow every answer goes viral.",
    cry: "DING DING",
  },
  glitchpup: {
    id: "glitchpup",
    evolvesTo: "glitchhound",
    evolveLevel: 35,
    name: "Glitchpup",
    types: ["ai", "chaos"],
    hp: 60,
    atk: 72,
    def: 56,
    spd: 80,
    catch: 40,
    bounty: 34,
    moves: ["promptJab", "flashCrash", "rateLimit", "fineTune"],
    learnset: [{ level: 1, id: "promptJab" }, { level: 1, id: "flashCrash" }, { level: 30, id: "rateLimit" }, { level: 31, id: "fineTune" }, { level: 34, id: "deepfake" }],
    blurb: "A good dog rendered badly. Half of it arrives a frame late.",
    cry: "wrf-wrf",
  },
  glitchhound: {
    id: "glitchhound",
    name: "Glitchhound",
    types: ["ai", "chaos"],
    hp: 84,
    atk: 104,
    def: 74,
    spd: 100,
    catch: 18,
    bounty: 60,
    moves: ["deepfake", "flashCrash", "rateLimit", "fineTune"],
    moveUpgrades: { promptJab: "deepfake" },
    blurb: "Its bark lands before its bite, and both land twice.",
    cry: "WRRRZZT",
  },
  fanbit: {
    id: "fanbit",
    evolvesTo: "fanstorm",
    evolveLevel: 35,
    name: "Fanbit",
    types: ["ai", "burn"],
    hp: 56,
    atk: 70,
    def: 62,
    spd: 70,
    catch: 40,
    bounty: 34,
    moves: ["promptJab", "emberTip", "gasWar", "heatMap"],
    learnset: [{ level: 1, id: "promptJab" }, { level: 1, id: "emberTip" }, { level: 30, id: "gasWar" }, { level: 31, id: "heatMap" }, { level: 34, id: "priorityFee" }],
    blurb: "A cooling fan that overheated once and liked it.",
    cry: "whirrr",
  },
  fanstorm: {
    id: "fanstorm",
    name: "Fanstorm",
    types: ["ai", "burn"],
    hp: 82,
    atk: 106,
    def: 84,
    spd: 88,
    catch: 18,
    bounty: 60,
    moves: ["neuralBeam", "priorityFee", "gasWar", "heatMap"],
    moveUpgrades: { promptJab: "neuralBeam", emberTip: "priorityFee" },
    blurb: "Three fans, one furnace. The whole grid gets warmer when it trains.",
    cry: "VRRRRRM",
  },
  ledmoth: {
    id: "ledmoth",
    evolvesTo: "rackmoth",
    evolveLevel: 34,
    name: "Ledmoth",
    types: ["ai", "signal"],
    hp: 58,
    atk: 64,
    def: 58,
    spd: 82,
    catch: 42,
    bounty: 34,
    moves: ["pingBurst", "promptJab", "packetLoss", "fineTune"],
    learnset: [{ level: 1, id: "pingBurst" }, { level: 1, id: "promptJab" }, { level: 30, id: "packetLoss" }, { level: 31, id: "fineTune" }, { level: 33, id: "pingSpike" }],
    blurb: "Drawn to status lights. Its wings are a circuit board somebody forgot to recycle.",
    cry: "blip",
  },
  rackmoth: {
    id: "rackmoth",
    name: "Rackmoth",
    types: ["ai", "signal"],
    hp: 80,
    atk: 90,
    def: 78,
    spd: 104,
    catch: 20,
    bounty: 58,
    moves: ["pingSpike", "backprop", "packetLoss", "fineTune"],
    moveUpgrades: { pingBurst: "pingSpike", promptJab: "backprop" },
    blurb: "Every scale on its wings blinks a different server's uptime.",
    cry: "BLIP-BLIP",
  },
  neuralith: {
    id: "neuralith",
    name: "Neuralith",
    types: ["ai", "moon"],
    hp: 106,
    atk: 112,
    def: 98,
    spd: 96,
    catch: 8,
    bounty: 130,
    moves: ["singularity", "moonbeam", "rateLimit", "lunarRest"],
    blurb: "A monolith of pure weights, orbiting its own thoughts. It dreams in the south subnet.",
    cry: "HMMMMMM",
  },
  pendbat: {
    id: "pendbat",
    evolvesTo: "stuckwing",
    evolveLevel: 33,
    name: "Pendbat",
    types: ["chaos", "rug"],
    hp: 58,
    atk: 66,
    def: 54,
    spd: 86,
    catch: 42,
    bounty: 34,
    moves: ["rumorMill", "exitScam", "spreadFud", "darkPool"],
    learnset: [{ level: 1, id: "rumorMill" }, { level: 1, id: "exitScam" }, { level: 30, id: "spreadFud" }, { level: 31, id: "darkPool" }, { level: 32, id: "mempoolCrush" }],
    blurb: "Hangs in the mempool for weeks. Swears the fee was high enough.",
    cry: "eek?",
  },
  stuckwing: {
    id: "stuckwing",
    name: "Stuckwing",
    types: ["chaos", "rug"],
    hp: 80,
    atk: 96,
    def: 74,
    spd: 106,
    catch: 20,
    bounty: 58,
    moves: ["mempoolCrush", "exitScam", "spreadFud", "darkPool"],
    moveUpgrades: { rumorMill: "mempoolCrush" },
    blurb: "Its wings are plastered with transactions that never confirmed. They flap anyway.",
    cry: "SKREEEK",
  },
  shroomint: {
    id: "shroomint",
    evolvesTo: "shroomancer",
    evolveLevel: 34,
    name: "Shroomint",
    types: ["viral", "cope"],
    hp: 70,
    atk: 58,
    def: 68,
    spd: 48,
    catch: 44,
    bounty: 32,
    moves: ["napTime", "toxicThread", "glowSpores", "touchReality"],
    learnset: [{ level: 1, id: "napTime" }, { level: 1, id: "toxicThread" }, { level: 30, id: "glowSpores" }, { level: 31, id: "touchReality" }, { level: 33, id: "doomscroll" }],
    blurb: "Mints a new spore every time someone looks at it. Very calm about it.",
    cry: "puff",
  },
  shroomancer: {
    id: "shroomancer",
    name: "Shroomancer",
    types: ["viral", "cope"],
    hp: 96,
    atk: 86,
    def: 94,
    spd: 64,
    catch: 20,
    bounty: 58,
    moves: ["hypeTrain", "toxicThread", "glowSpores", "touchReality"],
    moveUpgrades: { napTime: "hypeTrain" },
    blurb: "A mushroom sage whose spores trend in every cave at once.",
    cry: "PFFFOOM",
  },
  hashgrub: {
    id: "hashgrub",
    evolvesTo: "asicarab",
    evolveLevel: 33,
    name: "Hashgrub",
    types: ["ai", "hodl"],
    hp: 62,
    atk: 60,
    def: 78,
    spd: 44,
    catch: 44,
    bounty: 32,
    moves: ["frostNibble", "promptJab", "captchaWall", "stalactite"],
    learnset: [{ level: 1, id: "frostNibble" }, { level: 1, id: "promptJab" }, { level: 30, id: "captchaWall" }, { level: 31, id: "stalactite" }, { level: 32, id: "iceAge" }],
    blurb: "Chews through blocks one hash at a time. Cold-blooded, warm pins.",
    cry: "krnch",
  },
  asicarab: {
    id: "asicarab",
    name: "Asicarab",
    types: ["ai", "hodl"],
    hp: 86,
    atk: 94,
    def: 110,
    spd: 58,
    catch: 20,
    bounty: 58,
    moves: ["iceAge", "neuralBeam", "captchaWall", "stalactite"],
    moveUpgrades: { frostNibble: "iceAge", promptJab: "neuralBeam" },
    blurb: "A beetle with a mining chip for a shell. It has never once turned off.",
    cry: "KRRRZZ",
  },
  geodeggo: {
    id: "geodeggo",
    evolvesTo: "geodrake",
    evolveLevel: 35,
    name: "Geodeggo",
    types: ["hodl"],
    hp: 66,
    atk: 56,
    def: 88,
    spd: 36,
    catch: 46,
    bounty: 32,
    moves: ["stalactite", "diamondGrip", "frostNibble", "coldStorage"],
    learnset: [{ level: 1, id: "stalactite" }, { level: 1, id: "diamondGrip" }, { level: 30, id: "frostNibble" }, { level: 31, id: "coldStorage" }, { level: 34, id: "coldWallet" }],
    blurb: "An egg that is also a rock that is also full of crystals. Nobody knows which came first.",
    cry: "tok",
  },
  geodrake: {
    id: "geodrake",
    name: "Geodrake",
    types: ["hodl", "chaos"],
    hp: 88,
    atk: 102,
    def: 104,
    spd: 70,
    catch: 18,
    bounty: 60,
    moves: ["stalactite", "diamondGrip", "blizzardBag", "mempoolCrush"],
    moveUpgrades: { frostNibble: "blizzardBag", coldStorage: "mempoolCrush" },
    blurb: "Hatched from a geode after a very long hold. The crystals kept growing on its back.",
    cry: "GROOAR",
  },
  shrimpling: {
    id: "shrimpling",
    evolvesTo: "krillionaire",
    evolveLevel: 33,
    name: "Shrimpling",
    types: ["liquid"],
    hp: 52,
    atk: 64,
    def: 52,
    spd: 84,
    catch: 46,
    bounty: 30,
    moves: ["dipRain", "flashLoan", "pearlShield", "riptide"],
    learnset: [{ level: 1, id: "dipRain" }, { level: 1, id: "flashLoan" }, { level: 30, id: "pearlShield" }, { level: 31, id: "riptide" }, { level: 32, id: "coralCrush" }],
    blurb: "The smallest holder in the lagoon. It is so proud of its bag.",
    cry: "snip",
  },
  krillionaire: {
    id: "krillionaire",
    name: "Krillionaire",
    types: ["liquid", "flex"],
    hp: 78,
    atk: 96,
    def: 74,
    spd: 102,
    catch: 20,
    bounty: 62,
    moves: ["coralCrush", "flashLoan", "goldChain", "riptide"],
    moveUpgrades: { dipRain: "coralCrush", pearlShield: "goldChain" },
    blurb: "Started as a shrimp. Now wears the whole reef as jewelry.",
    cry: "SNIP-SNAP",
  },
  sidecrab: {
    id: "sidecrab",
    evolvesTo: "crabstack",
    evolveLevel: 34,
    name: "Sidecrab",
    types: ["liquid", "chaos"],
    hp: 60,
    atk: 74,
    def: 80,
    spd: 50,
    catch: 44,
    bounty: 32,
    moves: ["riptide", "flashCrash", "pearlShield", "rumorMill"],
    learnset: [{ level: 1, id: "riptide" }, { level: 1, id: "flashCrash" }, { level: 30, id: "pearlShield" }, { level: 31, id: "rumorMill" }, { level: 33, id: "coralCrush" }],
    blurb: "Only ever walks sideways. So does the chart on its shell.",
    cry: "click-clack",
  },
  crabstack: {
    id: "crabstack",
    name: "Crabstack",
    types: ["liquid", "chaos"],
    hp: 84,
    atk: 104,
    def: 110,
    spd: 58,
    catch: 20,
    bounty: 58,
    moves: ["coralCrush", "flashCrash", "pearlShield", "mempoolCrush"],
    moveUpgrades: { riptide: "coralCrush", rumorMill: "mempoolCrush" },
    blurb: "A tower of crabs holding a tower of coins. Neither will ever sell.",
    cry: "CLACK-CLACK",
  },
  jellyfud: {
    id: "jellyfud",
    evolvesTo: "medusell",
    evolveLevel: 34,
    name: "Jellyfud",
    types: ["liquid", "rug"],
    hp: 64,
    atk: 60,
    def: 58,
    spd: 72,
    catch: 42,
    bounty: 32,
    moves: ["riptide", "spreadFud", "honeypot", "walletDrainer"],
    learnset: [{ level: 1, id: "riptide" }, { level: 1, id: "spreadFud" }, { level: 30, id: "honeypot" }, { level: 31, id: "walletDrainer" }, { level: 33, id: "toxicThread" }],
    blurb: "Drifts through the reef whispering doubts. Its sting itches for days.",
    cry: "blub",
  },
  medusell: {
    id: "medusell",
    name: "Medusell",
    types: ["liquid", "rug"],
    hp: 92,
    atk: 88,
    def: 80,
    spd: 92,
    catch: 20,
    bounty: 60,
    moves: ["slippageSurge", "spreadFud", "honeypot", "walletDrainer"],
    moveUpgrades: { riptide: "slippageSurge" },
    blurb: "Queen of the sell wall. One look at her red candles and whole schools panic.",
    cry: "BLOOORB",
  },
  pearlclam: {
    id: "pearlclam",
    name: "Pearlclam",
    types: ["liquid", "moon"],
    hp: 76,
    atk: 62,
    def: 104,
    spd: 42,
    catch: 30,
    bounty: 44,
    moves: ["tidalPull", "pearlShield", "moonLullaby", "riptide"],
    blurb: "Keeps a tiny moon inside its shell. Opens only when the tide is right.",
    cry: "clam.",
  },
  lunapup: {
    id: "lunapup",
    evolvesTo: "lunawolf",
    evolveLevel: 34,
    name: "Lunapup",
    types: ["moon"],
    hp: 58,
    atk: 68,
    def: 56,
    spd: 84,
    catch: 42,
    bounty: 34,
    moves: ["orbitKick", "tidalPull", "moonLullaby", "rocketFuel"],
    learnset: [{ level: 1, id: "orbitKick" }, { level: 1, id: "tidalPull" }, { level: 30, id: "moonLullaby" }, { level: 31, id: "rocketFuel" }, { level: 33, id: "moonbeam" }],
    blurb: "Chases its own crescent every night. Catches it about once a month.",
    cry: "awoo",
  },
  lunawolf: {
    id: "lunawolf",
    name: "Lunawolf",
    types: ["moon", "cope"],
    hp: 84,
    atk: 100,
    def: 76,
    spd: 106,
    catch: 20,
    bounty: 60,
    moves: ["orbitKick", "moonbeam", "moonLullaby", "rocketFuel"],
    moveUpgrades: { tidalPull: "moonbeam" },
    blurb: "Howls at the chart until it goes up. Has not been wrong yet, it says.",
    cry: "AWOOOOO",
  },
  rocketot: {
    id: "rocketot",
    evolvesTo: "rocketitan",
    evolveLevel: 36,
    name: "Rocketot",
    types: ["moon", "flex"],
    hp: 56,
    atk: 74,
    def: 60,
    spd: 76,
    catch: 42,
    bounty: 34,
    moves: ["orbitKick", "quickFlip", "rocketFuel", "stardust"],
    learnset: [{ level: 1, id: "orbitKick" }, { level: 1, id: "quickFlip" }, { level: 30, id: "rocketFuel" }, { level: 31, id: "stardust" }, { level: 35, id: "wenMoon" }],
    blurb: "A toddler-sized rocket that asks 'wen' about everything.",
    cry: "vroom!",
  },
  rocketitan: {
    id: "rocketitan",
    name: "Rocketitan",
    types: ["moon", "flex"],
    hp: 88,
    atk: 104,
    def: 90,
    spd: 86,
    catch: 16,
    bounty: 64,
    moves: ["wenMoon", "flexCloud", "rocketFuel", "airdropStrike"],
    moveUpgrades: { orbitKick: "wenMoon", quickFlip: "flexCloud", stardust: "airdropStrike" },
    blurb: "Grew up, bolted on arms and finally answered its own question.",
    cry: "KA-BOOOM",
  },
  nimbaa: {
    id: "nimbaa",
    evolvesTo: "nimbram",
    evolveLevel: 34,
    name: "Nimbaa",
    types: ["moon", "cope"],
    hp: 72,
    atk: 56,
    def: 70,
    spd: 58,
    catch: 44,
    bounty: 32,
    moves: ["stardust", "napTime", "moonLullaby", "lunarRest"],
    learnset: [{ level: 1, id: "stardust" }, { level: 1, id: "napTime" }, { level: 30, id: "moonLullaby" }, { level: 31, id: "lunarRest" }, { level: 33, id: "craterSlam" }],
    blurb: "Its wool is a small cloud. Counting it puts everyone to sleep, including it.",
    cry: "baa~",
  },
  nimbram: {
    id: "nimbram",
    name: "Nimbram",
    types: ["moon", "hodl"],
    hp: 96,
    atk: 92,
    def: 96,
    spd: 72,
    catch: 20,
    bounty: 58,
    moves: ["craterSlam", "iceAge", "moonLullaby", "lunarRest"],
    moveUpgrades: { stardust: "craterSlam", napTime: "iceAge" },
    blurb: "A storm-cloud ram with frozen horns. It headbutts the weather into holding.",
    cry: "BAAAAM",
  },
  craterling: {
    id: "craterling",
    name: "Craterling",
    types: ["moon", "hodl"],
    hp: 78,
    atk: 70,
    def: 96,
    spd: 40,
    catch: 32,
    bounty: 42,
    moves: ["craterSlam", "gravityWell", "diamondGrip", "stardust"],
    blurb: "A pebble of moon that fell off and never went back. Every dent is a story.",
    cry: "tunk",
  },
  selenarch: {
    id: "selenarch",
    name: "Selenarch",
    types: ["moon", "hodl"],
    hp: 108,
    atk: 104,
    def: 102,
    spd: 100,
    catch: 8,
    bounty: 130,
    moves: ["moonbeam", "blizzardBag", "lunarRest", "moonLullaby"],
    blurb: "The owl who keeps the moon's ledger. Its wings are two crescents of pure silver.",
    cry: "HOOOOOOM",
  },  ...SIDECHAIN_SPECIES,
};

// Level-up offers after the starting kit (learnsetsLate.ts) join each learnset, in level order.
for (const [id, offers] of Object.entries(LEVEL_UP) as [SpeciesId, readonly (readonly [number, string])[]][]) {
  const sp = SPECIES[id];
  sp.learnset = [...(sp.learnset ?? []), ...offers.map(([level, move]) => ({ level, id: move }))].sort((a, b) => a.level - b.level);
}

export const STARTERS: SpeciesId[] = ["shibonk", "bogfiend", "wojakle"];

export function speciesById(id: SpeciesId): SpeciesDef {
  return SPECIES[id];
}
